// Input (DESIGN §3.6.3) für Text: text, email, password (Show/Hide), search (Lupe, ×, Esc leert),
// url, tel. Zahlen, Geld und Prozent: NumberInput. Höhe 40 (md) bzw. 48 (lg), Fläche `input`.
import {
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { EyeIcon, EyeSlashIcon, MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { CharCount } from './_lib/CharCount';
import { fieldFrame, fieldIcon, fieldInput, type FieldSize } from './_lib/fieldStyles';
import { useFieldProps } from './_lib/fieldContext';
import { mergeRefs, setNativeValue } from './_lib/mergeRefs';
import { IconButton } from './IconButton';
import { cn } from '../basis/kern';

export type InputType = 'text' | 'email' | 'password' | 'search' | 'url' | 'tel';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  ref?: Ref<HTMLInputElement>;
  type?: InputType;
  size?: FieldSize;
  /** Icon links (16, `fg-secondary`); bei `search` Standard die Lupe. */
  leading?: ReactNode;
  /** Inhalt rechts im Feld (Icon, Einheit, IconButton). */
  trailing?: ReactNode;
  /** Zeichenzähler unter dem Feld (braucht `maxLength` oder zählt nur). */
  showCount?: boolean;
  /** Klassen für den Rahmen (der `className` geht ebenfalls an den Rahmen). */
  frameClassName?: string;
  /** Name des Leeren-Knopfs bei `search`. */
  clearLabel?: string;
}

export function Input({
  ref,
  type = 'text',
  size = 'md',
  leading,
  trailing,
  showCount = false,
  className,
  frameClassName,
  clearLabel = 'Clear search',
  readOnly,
  onChange,
  onKeyDown,
  ...rest
}: InputProps) {
  const inner = useRef<HTMLInputElement>(null);
  const counterId = useId();
  const [revealed, setRevealed] = useState(false);
  const [uncontrolledLength, setUncontrolledLength] = useState(
    String(rest.defaultValue ?? '').length,
  );
  const field = useFieldProps(rest);
  const controlled = rest.value !== undefined;
  const length = controlled ? String(rest.value ?? '').length : uncontrolledLength;
  const isSearch = type === 'search';
  const hasValue = length > 0;

  const clear = () => {
    if (inner.current) {
      setNativeValue(inner.current, '');
      inner.current.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (isSearch && event.key === 'Escape' && hasValue) {
      event.preventDefault();
      event.stopPropagation();
      clear();
    }
  };

  const leadingNode = leading ?? (isSearch ? <MagnifyingGlassIcon /> : null);
  const describedBy = cn(field['aria-describedby'], showCount && counterId) || undefined;

  const input = (
    <input
      {...rest}
      ref={mergeRefs(ref, inner)}
      id={field.id}
      required={field.required}
      aria-invalid={field['aria-invalid']}
      aria-describedby={describedBy}
      type={type === 'password' && revealed ? 'text' : type === 'search' ? 'search' : type}
      readOnly={readOnly}
      onChange={(event) => {
        if (!controlled) setUncontrolledLength(event.target.value.length);
        onChange?.(event);
      }}
      onKeyDown={handleKeyDown}
      className={cn(fieldInput, isSearch && '[&::-webkit-search-cancel-button]:appearance-none')}
    />
  );

  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-1', showCount && 'items-end')}>
      <div
        data-size={size}
        data-readonly={readOnly || undefined}
        className={cn(
          fieldFrame(size),
          readOnly && 'border-transparent! bg-transparent px-0 focus-within:outline-none',
          className,
          frameClassName,
        )}
      >
        {leadingNode ? (
          <span aria-hidden="true" className={fieldIcon}>
            {leadingNode}
          </span>
        ) : null}
        {input}
        {isSearch && hasValue && !readOnly ? (
          <IconButton
            label={clearLabel}
            icon={<XIcon />}
            variant="ghost"
            surface="tile"
            size="xs"
            tooltip={false}
            onClick={clear}
            className="-mr-1.5"
          />
        ) : null}
        {type === 'password' && !readOnly ? (
          <IconButton
            label={revealed ? 'Hide password' : 'Show password'}
            icon={revealed ? <EyeSlashIcon /> : <EyeIcon />}
            variant="ghost"
            surface="tile"
            size="sm"
            tooltip={false}
            onClick={() => setRevealed((v) => !v)}
            className="-mr-2"
          />
        ) : null}
        {trailing ? <span className={cn(fieldIcon, 'text-fg-tertiary')}>{trailing}</span> : null}
      </div>
      {showCount ? <CharCount id={counterId} length={length} max={rest.maxLength} /> : null}
    </div>
  );
}
